import { Fragment, useMemo } from 'react';
import { View } from 'react-native';
import { Text, useTheme } from '@ui-kitten/components';

import ResponsiveLayout from '../../Components/ScreenWrapper/ResponsiveLayout';
import SwitchTabOrderType from '../../Components/SwitchTabOrderType';
import constants from '../../Config/constants';
import useMealPlan from '../../Hooks/useMealPlan';
import themeStyle from '../../Theme/styles';

import CheckoutRightContent from './CheckoutRightContent';
import useCheckout from './controller/useCheckout';
import useCheckoutHandler from './controller/useCheckoutHandler';
import DeliveryEstimate from './DeliveryEstimate';
import DropOffOptions from './DropOffOptions';
import PickupLocationRow from './PickupLocationRow';
import PlacingOrderModal from './PlacingOrderModal';
import ThirdPartyPickup from './ThirdPartyPickup';
import UserDetailsRow from './UserDetailsRow';
import VehicleInformation from './VehicleInformation';
import VehicleOptions from './VehicleOptions';

const Checkout = () => {
  const theme = useTheme();
  const { isMealPlan } = useMealPlan();
  const { checkoutData, checkoutInfo, checkoutItems, formDeliveryAddress } =
    useCheckoutHandler();
  const {
    ref,
    state,
    deliveryQuotation,
    hasBulk,
    isStoreHoursLoading,
    storeHours,
    storeHoursError,
    storeMealPlanDiscount,
    goBackToStore,
    onPlaceOrder,
    onRequestCheckout,
    refetchStoreHours,
    togglePlaceOrder,
  } = useCheckout();

  const _title = useMemo(() => {
    if (constants.isWeb) return undefined;
    return (
      <View style={themeStyle.fitWidthContainer}>
        <Text appearance="hint">Checkout</Text>
        <Text category="h6" numberOfLines={1}>
          {checkoutInfo.store_name}
        </Text>
      </View>
    );
  }, [checkoutInfo.store_name]);

  const titleProps = useMemo(() => {
    if (constants.isWeb) return undefined;
    return { iconColor: theme['color-success-500'] };
  }, [theme]);

  return (
    <Fragment>
      <ResponsiveLayout
        title={_title}
        titleProps={titleProps}
        LeftComponent={
          <View
            style={[
              !constants.isWeb && themeStyle.pageHorizontalSpacing,
              !constants.isWeb && themeStyle.spacingTopMedium,
            ]}
          >
            {!isMealPlan && <SwitchTabOrderType />}
            <PickupLocationRow disabled={state.isPlacingOrder} />
            <UserDetailsRow />
            <DropOffOptions
              selectedAddress={formDeliveryAddress}
              disabled={state.isPlacingOrder}
            />
            <DeliveryEstimate
              // @ts-ignore: This component is JS file
              disabled={state.isPlacingOrder}
              isStoreHoursLoading={isStoreHoursLoading}
              ref={ref.deliveryEstimate}
              storeHours={storeHours}
              storeHoursError={storeHoursError}
              storeOffDates={checkoutInfo.store_off_dates}
              onRetry={refetchStoreHours}
            />
            <VehicleOptions
              disabled={deliveryQuotation.loading || state.isPlacingOrder}
              hasBulk={hasBulk}
            />
            <ThirdPartyPickup disabled={state.isPlacingOrder} />
            <VehicleInformation disabled={state.isPlacingOrder} />
          </View>
        }
        RightComponent={
          <CheckoutRightContent
            deliveryQuotation={deliveryQuotation}
            isPlacingOrder={state.isPlacingOrder}
            mealPlanDiscount={storeMealPlanDiscount}
            onBackToStore={goBackToStore}
            onPlaceOrder={onPlaceOrder}
          />
        }
        rightComponentWidth={400}
        withTitleAnimation={false}
        withFooter={false}
      />

      <PlacingOrderModal
        data={{
          storeId: checkoutInfo.store_id,
          storeName: checkoutInfo.store_name,
          cart: checkoutItems,
          details: checkoutData,
        }}
        visible={state.isPlacingOrder}
        onRequest={onRequestCheckout}
        onReSchedule={ref.deliveryEstimate.current?.showScheduleListModal}
        togglePlaceOrder={togglePlaceOrder}
      />
    </Fragment>
  );
};

export default Checkout;
